import { useEffect, useState } from "react";
import { Loader2, Calendar, Clock, MapPin, Check, X } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LanguageContext";
import { toast } from "sonner";

export function Appointments() {
  const { user } = useAuth();
  const { t, L } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const isStaff = user.role === "admin" || user.role === "agent";

  const load = async () => { setLoading(true); const { data } = await api.get("/appointments"); setItems(data.items); setLoading(false); };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => { await api.patch(`/appointments/${id}`, { status }); toast.success("Updated"); load(); };

  const statusColor = {
    pending: "text-amber-400 border-amber-500/40", confirmed: "text-emerald-400 border-emerald-500/40",
    cancelled: "text-red-400 border-red-500/40", completed: "text-blue-400 border-blue-500/40",
  };

  if (loading) return <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-primary" size={28} /></div>;

  return (
    <div>
      <h1 className="font-serif text-4xl font-light mb-2">{t("dash.appointments")}</h1>
      <p className="text-muted-foreground font-light mb-8">{items.length} scheduled</p>

      {items.length === 0 ? (
        <p className="py-20 text-center text-muted-foreground font-light" data-testid="no-appointments">{t("dash.noAppointments")}</p>
      ) : (
        <div className="space-y-3" data-testid="appointments-list">
          {items.map((a) => (
            <div key={a.id} className="flex flex-col sm:flex-row sm:items-center gap-4 bg-card border border-border p-5" data-testid={`appointment-${a.id}`}>
              {a.property?.images?.[0] && <img src={a.property.images[0]} alt="" className="w-full sm:w-24 h-24 object-cover" />}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs px-2 py-0.5 border capitalize ${statusColor[a.status] || ""}`}>{a.status}</span>
                  <span className="label-caps text-muted-foreground text-[0.6rem] capitalize">{a.type}</span>
                </div>
                {a.property && <p className="font-serif text-xl">{L(a.property.title)}</p>}
                <p className="text-sm text-muted-foreground">{isStaff ? a.name : "Your appointment"} · {a.email}</p>
                <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5"><Calendar size={14} className="text-primary" /> {a.date}</span>
                  <span className="flex items-center gap-1.5"><Clock size={14} className="text-primary" /> {a.time}</span>
                </div>
              </div>
              {isStaff && a.status === "pending" && (
                <div className="flex gap-2">
                  <button onClick={() => setStatus(a.id, "confirmed")} className="flex items-center gap-1.5 px-3 py-2 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 transition-colors text-xs" data-testid={`confirm-${a.id}`}><Check size={14} /> Confirm</button>
                  <button onClick={() => setStatus(a.id, "cancelled")} className="flex items-center gap-1.5 px-3 py-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors text-xs" data-testid={`cancel-${a.id}`}><X size={14} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
